// frontend/src/components/modals/TwoFactorModal.jsx
// 2FA setup and management modal

import React, { useState, useEffect } from 'react';
import { FiShield, FiCopy, FiCheck, FiX } from 'react-icons/fi';
import api from '../../services/api';

export default function TwoFactorModal({ action, onClose, onComplete }) {
  const [step, setStep] = useState(1);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (action === 'enable') {
      initiate2FASetup();
    }
  }, [action]);

  const initiate2FASetup = async () => {
    try {
      setLoading(true);
      const response = await api.post('/auth/2fa/setup');
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
    } catch (error) {
      console.error('Failed to initiate 2FA setup:', error);
    } finally {
      setLoading(false);
    }
  };

  const verify2FA = async () => {
    try {
      setLoading(true);
      const response = await api.post('/auth/2fa/verify-setup', {
        code: verificationCode
      });
      setBackupCodes(response.data.backup_codes);
      setStep(3);
    } catch (error) {
      alert('Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async () => {
    try {
      setLoading(true);
      await api.post('/auth/2fa/disable', {
        code: verificationCode
      });
      onComplete();
    } catch (error) {
      alert('Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderEnableFlow = () => {
    switch (step) {
      case 1:
        return (
          <div className="two-factor-setup">
            <div className="setup-step">
              <h4>Step 1: Scan QR Code</h4>
              <p>Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)</p>
              
              {loading ? (
                <div className="qr-loading">Loading QR code...</div>
              ) : (
                <div className="qr-code-container">
                  <img src={qrCode} alt="2FA QR Code" />
                </div>
              )}
              
              <div className="manual-entry">
                <p>Can't scan? Enter this key manually:</p>
                <div className="secret-key">
                  <code>{secret}</code>
                  <button onClick={copySecret} className="copy-btn">
                    {copied ? <FiCheck /> : <FiCopy />}
                  </button>
                </div>
              </div>
              
              <button 
                className="btn btn-primary"
                onClick={() => setStep(2)}
                disabled={loading}
              >
                Next Step
              </button>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="two-factor-verify">
            <div className="setup-step">
              <h4>Step 2: Verify Setup</h4>
              <p>Enter the 6-digit code from your authenticator app:</p>
              
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="000000"
                maxLength={6}
                className="verification-input"
              />
              
              <div className="verify-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={verify2FA}
                  disabled={loading || verificationCode.length !== 6}
                >
                  {loading ? 'Verifying...' : 'Verify & Enable'}
                </button>
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="two-factor-complete">
            <div className="setup-step">
              <div className="success-icon">
                <FiShield size={48} />
              </div>
              <h4>2FA Enabled Successfully!</h4>
              
              <div className="backup-codes">
                <h5>Backup Codes</h5>
                <p>Save these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.</p>
                
                <div className="codes-grid">
                  {backupCodes.map((code, index) => (
                    <code key={index} className="backup-code">{code}</code>
                  ))}
                </div>
                
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    const codes = backupCodes.join('\n');
                    navigator.clipboard.writeText(codes);
                  }}
                >
                  <FiCopy size={16} />
                  Copy All Codes
                </button>
              </div>
              
              <button 
                className="btn btn-primary"
                onClick={onComplete}
              >
                Complete Setup
              </button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  const renderDisableFlow = () => (
    <div className="two-factor-disable">
      <div className="warning-icon">
        <FiShield size={48} />
      </div>
      <h4>Disable Two-Factor Authentication</h4>
      <p>This will remove the extra security layer from your account. Enter a code from your authenticator app to confirm:</p>
      
      <input
        type="text"
        value={verificationCode}
        onChange={(e) => setVerificationCode(e.target.value)}
        placeholder="000000"
        maxLength={6}
        className="verification-input"
      />
      
      <div className="disable-actions">
        <button 
          className="btn btn-secondary"
          onClick={onClose}
        >
          Cancel
        </button>
        <button 
          className="btn btn-danger"
          onClick={disable2FA}
          disabled={loading || verificationCode.length !== 6}
        >
          {loading ? 'Disabling...' : 'Disable 2FA'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content two-factor-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Two-Factor Authentication</h3>
          <button className="modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        
        <div className="modal-body">
          {action === 'enable' ? renderEnableFlow() : renderDisableFlow()}
        </div>
      </div>
    </div>
  );
}